import { useRef } from "react";
import { Box } from "@chakra-ui/react";

// Toggle the page-wide drag affordance. Kept at module scope: mutating global
// document.body inside a component trips the react-hooks immutability rule.
function setBodyDragging(on: boolean) {
  document.body.style.cursor = on ? "col-resize" : "";
  document.body.style.userSelect = on ? "none" : "";
}

type ResizeHandleProps = {
  width: number;
  onResize: (w: number) => void;
  onResetWidth: () => void;
};

// Drag-to-resize grip anchored to the right edge of a relatively-positioned
// table header cell. Double-click resets to the default width. Shared by
// SortHeader and by columns that are resizable but not sortable.
export default function ResizeHandle({ width, onResize, onResetWidth }: ResizeHandleProps) {
  const drag = useRef<{ startX: number; startWidth: number } | null>(null);

  const onMouseMove = (e: MouseEvent) => {
    if (!drag.current) return;
    const dx = e.clientX - drag.current.startX;
    onResize(drag.current.startWidth + dx);
  };
  const onMouseUp = () => {
    drag.current = null;
    document.removeEventListener("mousemove", onMouseMove);
    document.removeEventListener("mouseup", onMouseUp);
    setBodyDragging(false);
  };
  const startResize = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    drag.current = { startX: e.clientX, startWidth: width };
    document.addEventListener("mousemove", onMouseMove);
    document.addEventListener("mouseup", onMouseUp);
    setBodyDragging(true);
  };

  return (
    <Box
      position="absolute"
      top="20%"
      right={0}
      height="60%"
      width="3px"
      cursor="col-resize"
      bg="gray.300"
      onMouseDown={startResize}
      onClick={(e) => e.stopPropagation()}
      onDoubleClick={(e) => {
        e.stopPropagation();
        onResetWidth();
      }}
      _hover={{ bg: "blue.400", width: "5px" }}
    />
  );
}
